const axios = require('axios')
const {isNil, isEmpty} = require('ramda')
const isDefined = input => !isNil(input) && !isEmpty(input)
const logRequestDefault = false
const LOG_REQUESTS = isDefined(process.env.LOG_REQUESTS)
  ? process.env.LOG_REQUESTS
  : logRequestDefault

const log = (method, url, payload) => {
  if (LOG_REQUESTS) {
    console.debug(`${method.toUpperCase()} ${url}`, payload)
  }
}

const {axiosRetry, exponentialDelay} = require('../../vendors/axios-retry')

function onRetry(retryCount, error, requestConfig) {
  // If (LOG_REQUESTS) {
  console.debug(
    `Retry #${retryCount} for ${requestConfig.method} ${requestConfig.url}`
  )
  // }
}

axiosRetry(axios, {
  retries: 10,
  retryDelay: exponentialDelay,
  onRetry: onRetry,
  shouldResetTimeout: true,
})

const convertResult = input => {
  const {data, status, headers, config, statusText} = input
  const xhrStatus =
    input.status < 200 || input.status >= 300 ? 'error' : 'success'
  return {
    data,
    status,
    headers,
    config,
    statusText,
    xhrStatus,
  }
}

const $axios = {
  get: async (url, config) => {
    log('get', url, {config})
    const result = await axios.get(url, config)
    return convertResult(result)
  },
  put: async (url, data, config) => {
    log('put', url, {data, config})
    const result = await axios.put(url, data, config)
    return convertResult(result)
  },
  patch: async (url, data, config) => {
    log('patch', url, {data, config})
    const result = await axios.patch(url, data, config)
    return convertResult(result)
  },
  delete: async (url, config) => {
    log('delete', url, {config})
    const result = await axios.delete(url, config)
    return convertResult(result)
  },
  request: async config => {
    console.error('REQUEST CALLED')
    const result = await axios.request(config)
    return convertResult(result)
  },
  post: async (url, data, config) => {
    log('post', url, {data, config})
    const result = await axios.post(url, data, config)
    return convertResult(result)
  },
}

angular.module('app.api').factory('api', [
  '$timeout',
  '$window',
  'logger',
  '$http',
  function api($timeout, $window, logger, $http) {
    let config = {}
    let inMemoryToken = {
      token: '',
      expiry: 0,
    }
    let refreshTimeOut

    const prefix = process.env.API_PREFIX || process.env.PREFIX || 'v1'
    // eslint-disable-next-line unicorn/consistent-function-scoping
    const apiUrl = path => {
      return `${process.env.LEXSTEP_URL}/api/${process.env.API_PREFIX}/${path}`
    }

    const httpOptionsWithCredentials = {
      withCredentials: true,
    }

    const getHandler = retry => {
      if (retry === null || retry === undefined || retry) {
        return $axios
      }

      return $http
    }

    const privateApi = {
      logOut: async () => {
        const result = await Api.post(
          'auth/logout',
          {},
          httpOptionsWithCredentials
        )
        return result
      },
      refreshToken: () => {
        try {
          console.debug('getting refresh token from local storage:')
          const tokenResponse = $window.localStorage.getItem('token')
          if (tokenResponse) {
            const parsed = JSON.parse(tokenResponse)
            console.debug('token in local storage:', parsed)
            return Api.post('auth/refresh', {token: parsed.refreshToken}, true)
          }
        } catch (error) {
          console.error('failed to get refresh token from local storage', error)
        }

        return Api.post('auth/refresh', {}, httpOptionsWithCredentials)
      },
      verifyLoginUser: async response => {
        if (response.status === 401 || response.status === 404) {
          let user = $window.localStorage.getItem('user')
          user = user && JSON.parse(user)
          if (user) {
            try {
              const updated = await Api.get(
                apiUrl(`users/${user.id}`),
                config,
                true,
                true
              )

              if (updated.status === 200) {
                console.debug('updating current user in local storage')
                $window.localStorage.setItem(
                  'user',
                  JSON.stringify(updated.data)
                )
              }
            } catch (error) {
              if (error.status < 500 && error.status >= 400) {
                $window.localStorage.removeItem('token')
                // eslint-disable-next-line unicorn/no-null
                $window.localStorage.setItem('user', null)
                logger.notify('error', 'Please Login')
                $timeout(() => {
                  $window.location.href = '/signin'
                }, 2000)
              }
            }
          }
        }

        if (response.status === 403) {
          logger.notify(
            'error',
            `${response.statusText}, Not allowed to access Route: ${response.data.path}`
          )
        }

        if (response.status === -1 || response.status === 502) {
          logger.notify(
            'error',
            'A problem occurred when communicating with the LexStep.com server. Please try again. If the problem persists, please contact support.'
          )
          console.error('verifyLoginUser', response)
        }
      },
    }

    const handleApiError = async (error, endpoint, shouldHandle) => {
      console.debug('handleApiError:', error)
      if (!error) {
        throw new Error('No error object')
      }

      const errorIsFiveHundred =
        error.response.status >= 500 && error.response.status < 600
      if (endpoint === 'auth/login' || endpoint === 'auth/refresh') {
        throw error
      }

      if (shouldHandle) {
        if (error.response.status === -1 || error.response.status === 401) {
          await privateApi.verifyLoginUser(error)
        } else if (errorIsFiveHundred) {
          $window.location.href = '/502'
        } else {
          throw error
        }
      } else {
        console.error('handleApiError: should not verify login user')
        if (error.response.status === 401) {
          console.warn('ending session: 401 encountered')
          await Api.endSession()
        }
      }
    }

    const Api = {
      get: async (path, apiConfig, retry, throwOnError) => {
        const shouldHandleError =
          throwOnError === null ||
          throwOnError === undefined ||
          throwOnError === false
        try {
          const handler = getHandler(retry)
          const result = await handler.get(
            apiUrl(path),
            apiConfig ? angular.merge({}, config, apiConfig) : config
          )
          return convertResult(result)
        } catch (error) {
          handleApiError(error, shouldHandleError)
        }
      },
      post: async (path, payload, apiConfig, retry, throwOnError) => {
        try {
          const handler = getHandler(retry)
          const result = await handler.post(
            apiUrl(path),
            payload,
            apiConfig ? angular.merge({}, config, apiConfig) : config
          )
          return result
        } catch (error) {
          // eslint-disable-next-line no-negated-condition
          if (!throwOnError) {
            handleApiError(error, path)
          } else {
            throw error
          }
        }
      },
      put: async (path, payload, apiConfig, retry) => {
        try {
          const handler = getHandler(retry)
          const result = await handler.put(
            apiUrl(path),
            payload,
            apiConfig ? angular.merge({}, config, apiConfig) : config
          )
          return result
        } catch (error) {
          handleApiError(error)
        }
      },
      delete: async (path, apiConfig, retry) => {
        try {
          const handler = getHandler(retry)
          const result = await handler.delete(
            apiUrl(path),
            apiConfig ? angular.merge({}, config, apiConfig) : config
          )
          return result
        } catch (error) {
          handleApiError(error)
        }
      },
      verifySessionToken: () => {
        if (inMemoryToken.token !== '') return true
        const token = $window.localStorage.getItem('token')
        if (token) {
          Api.updateInMemoryToken(JSON.parse(token))
          return true
        }

        return false
      },
      startSession: tokenResponse => {
        Api.updateInMemoryToken(tokenResponse)
        const nextRefresh = (inMemoryToken.expiry - 60) * 1000
        $window.localStorage.setItem('token', JSON.stringify(tokenResponse))
        refreshTimeOut = $timeout(async () => {
          const response = await privateApi.refreshToken()
          if (response.status === 201) Api.startSession(response.data)
        }, nextRefresh)
      },
      endSession: async () => {
        inMemoryToken = {
          token: '',
          expiry: 0,
        }
        config = {}
        $window.localStorage.setItem('logout', Date.now())
        $timeout.cancel(refreshTimeOut)
        $window.localStorage.removeItem('token')
        $window.localStorage.removeItem('user')
        await privateApi.logOut()
        $window.location.href = '/signin'
      },
      refreshSession: async () => {
        return privateApi.refreshToken().then(
          ({data}) => {
            Api.startSession(data)
            return true
          },
          _error => {
            return false
          }
        )
      },
      updateInMemoryToken: tokenResponse => {
        inMemoryToken = {
          token: tokenResponse.id_token,
          expiry: tokenResponse.expires_in,
        }
        config = {
          headers: {
            Authorization: `Bearer ${inMemoryToken.token}`,
          },
        }
      },
    }
    return Api
  },
])



// WEBPACK FOOTER //
// ./scripts/shared/api/api.js